import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AuthedLayout } from '@/components/layout/AuthedLayout';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { RouteGuard } from '@/components/auth/RouteGuard';
import { LoginPage } from '@/components/auth/LoginPage';
import { TwoFactorPage } from '@/components/auth/TwoFactorPage';
import { AccessDeniedPage } from '@/components/auth/AccessDeniedPage';
import { NotFoundPage } from '@/components/shared/NotFoundPage';
import { RouteFallback } from '@/components/shared/RouteFallback';

// Every authed page is lazy-imported so the initial bundle
// is the auth layer + layout chrome only. The 14 feature chunks
// download on first navigation; subsequent revisits are cache hits.
// Pages export named identifiers — React.lazy needs a default, so we
// re-shape on import. The literal in each call (`./features/...Page`)
// must be a static string so Vite's tree analyser can split them.
const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const MerchantsPage = lazy(() =>
  import('@/features/merchants/MerchantsPage').then((m) => ({ default: m.MerchantsPage })),
);
const MerchantDetailPage = lazy(() =>
  import('@/features/merchants/MerchantDetailPage').then((m) => ({
    default: m.MerchantDetailPage,
  })),
);
const OnboardMerchantPage = lazy(() =>
  import('@/features/merchants/OnboardMerchantPage').then((m) => ({
    default: m.OnboardMerchantPage,
  })),
);
const InvitationsPage = lazy(() =>
  import('@/features/merchants/InvitationsPage').then((m) => ({ default: m.InvitationsPage })),
);
const DevicesPage = lazy(() =>
  import('@/features/devices/DevicesPage').then((m) => ({ default: m.DevicesPage })),
);
const DeviceDetailPage = lazy(() =>
  import('@/features/devices/DeviceDetailPage').then((m) => ({ default: m.DeviceDetailPage })),
);
const AgentReleasesPage = lazy(() =>
  import('@/features/agent-releases/AgentReleasesPage').then((m) => ({
    default: m.AgentReleasesPage,
  })),
);
const DealsModerationPage = lazy(() =>
  import('@/features/deals/DealsModerationPage').then((m) => ({ default: m.DealsModerationPage })),
);
const DealReportsQueuePage = lazy(() =>
  import('@/features/deals/DealReportsQueuePage').then((m) => ({
    default: m.DealReportsQueuePage,
  })),
);
const DealModerationDetailPage = lazy(() =>
  import('@/features/deals/DealModerationDetailPage').then((m) => ({
    default: m.DealModerationDetailPage,
  })),
);
const ReelsModerationPage = lazy(() =>
  import('@/features/reels/ReelsModerationPage').then((m) => ({
    default: m.ReelsModerationPage,
  })),
);
const ReelModerationDetailPage = lazy(() =>
  import('@/features/reels/ReelModerationDetailPage').then((m) => ({
    default: m.ReelModerationDetailPage,
  })),
);
const ReceiptsPage = lazy(() =>
  import('@/features/receipts/ReceiptsPage').then((m) => ({ default: m.ReceiptsPage })),
);
const ReceiptDetailPage = lazy(() =>
  import('@/features/receipts/ReceiptDetailPage').then((m) => ({ default: m.ReceiptDetailPage })),
);
const ParseReviewPage = lazy(() =>
  import('@/features/parse-review/ParseReviewPage').then((m) => ({ default: m.ParseReviewPage })),
);
const MetricsPage = lazy(() =>
  import('@/features/metrics/MetricsPage').then((m) => ({ default: m.MetricsPage })),
);
const AuditPage = lazy(() =>
  import('@/features/audit/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const UsersPage = lazy(() =>
  import('@/features/users/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const ConsumersPage = lazy(() =>
  import('@/features/consumers/ConsumersPage').then((m) => ({ default: m.ConsumersPage })),
);
const ConsumerDetailPage = lazy(() =>
  import('@/features/consumers/ConsumerDetailPage').then((m) => ({
    default: m.ConsumerDetailPage,
  })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

function lazyRoute(Element: React.ComponentType): React.ReactNode {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Element />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/login/2fa', element: <TwoFactorPage /> },
      { path: '/access-denied', element: <AccessDeniedPage /> },
    ],
  },
  {
    element: (
      <RouteGuard>
        <AuthedLayout />
      </RouteGuard>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: '/dashboard', element: lazyRoute(DashboardPage) },
      { path: '/merchants', element: lazyRoute(MerchantsPage) },
      { path: '/merchants/onboard', element: lazyRoute(OnboardMerchantPage) },
      { path: '/merchants/invitations', element: lazyRoute(InvitationsPage) },
      { path: '/merchants/:merchantId', element: lazyRoute(MerchantDetailPage) },
      { path: '/devices', element: lazyRoute(DevicesPage) },
      { path: '/devices/:deviceId', element: lazyRoute(DeviceDetailPage) },
      { path: '/agent-releases', element: lazyRoute(AgentReleasesPage) },
      { path: '/deals', element: lazyRoute(DealsModerationPage) },
      { path: '/deals/reports', element: lazyRoute(DealReportsQueuePage) },
      { path: '/deals/:dealId', element: lazyRoute(DealModerationDetailPage) },
      { path: '/reels', element: lazyRoute(ReelsModerationPage) },
      { path: '/reels/:reelId', element: lazyRoute(ReelModerationDetailPage) },
      { path: '/receipts', element: lazyRoute(ReceiptsPage) },
      { path: '/receipts/:receiptId', element: lazyRoute(ReceiptDetailPage) },
      { path: '/parse-review', element: lazyRoute(ParseReviewPage) },
      { path: '/metrics', element: lazyRoute(MetricsPage) },
      { path: '/audit', element: lazyRoute(AuditPage) },
      { path: '/users', element: lazyRoute(UsersPage) },
      { path: '/consumers', element: lazyRoute(ConsumersPage) },
      { path: '/consumers/:userId', element: lazyRoute(ConsumerDetailPage) },
      { path: '/settings', element: lazyRoute(SettingsPage) },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
